<script setup lang="ts">
import { ref, computed, watch } from "vue";
import MarkdownIt from "markdown-it";

const markdownModules = import.meta.glob("@/content/**/index.md", {
  query: "?raw",
  import: "default",
  eager: false,
}) as Record<string, () => Promise<string>>;

const md = new MarkdownIt({
  html: true,
});

const html = ref("");

const props = defineProps<{
  open: boolean;
  markdownPath?: string;
  content?: string;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const selectedMarkdown = computed(() => props.markdownPath || "emissionOverview");

const loadMarkdown = async (path: string) => {
  const availablePaths = Object.keys(markdownModules);

  const normalizedPath = path.toLowerCase();

  const moduleKey = availablePaths.find((key) => {
    const normalizedKey = key.toLowerCase();

    return (
      normalizedKey.includes(`/${normalizedPath}/`) ||
      normalizedKey.includes(`\\${normalizedPath}\\`) ||
      normalizedKey.endsWith(`${normalizedPath}/index.md`) ||
      normalizedKey.includes(`content/${normalizedPath}/`) ||
      normalizedKey.includes(`/${normalizedPath}/index.md`)
    );
  });

  if (moduleKey) {
    const moduleLoader = markdownModules[moduleKey];
    try {
      const markdownContent = await moduleLoader();
      html.value = md.render(markdownContent);
    } catch (error) {
      console.error(`Failed to load markdown from ${path}:`, error);
      html.value = `<p>Error loading markdown content from "${path}"</p>`;
    }
  } else {
    html.value = `<p>Content not found.</p>`;
  }
};

watch(
  () => props.content,
  (raw) => {
    if (raw !== undefined) html.value = md.render(raw);
  },
  { immediate: true }
);

watch(
  selectedMarkdown,
  (newPath) => {
    if (props.content === undefined) loadMarkdown(newPath);
  },
  { immediate: true }
);
</script>

<template>
  <div class="max-h-[70vh] overflow-y-auto p-3">
    <article
      class="markdown-body markdown-body--modal"
      v-html="html"></article>
  </div>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

:deep(.markdown-body) {
  color-scheme: light;
  background-color: transparent !important;
  color: var(--color-type-default) !important;
}

:deep(.markdown-body p) {
  background-color: transparent;
}

:deep(.markdown-body h1),
:deep(.markdown-body h2),
:deep(.markdown-body h3),
:deep(.markdown-body h4),
:deep(.markdown-body h5),
:deep(.markdown-body h6) {
  color: var(--color-type-default) !important;
}

:deep(.markdown-body a) {
  color: var(--color-app-blue-600) !important;
}

:deep(.markdown-body code) {
  background-color: rgba(175, 184, 193, 0.2) !important;
  color: var(--color-type-default) !important;
}

:deep(.markdown-body pre) {
  background-color: var(--color-slate-100) !important;
  color: var(--color-type-default) !important;
}

:deep(.markdown-body pre code) {
  background-color: transparent !important;
  color: var(--color-type-default) !important;
}

:deep(.markdown-body blockquote) {
  color: var(--color-type-muted) !important;
  border-left-color: var(--color-slate-300) !important;
}

:deep(.markdown-body ul) {
  list-style-type: disc;
  color : var(--color-type-default) !important;
}

:deep(.markdown-body ul ul) {
  list-style-type: circle;
  color : var(--color-type-default) !important;
}

:deep(.markdown-body ol) {
  list-style-type: decimal;
  color : var(--color-type-default) !important;
}

:deep(.markdown-body table th),
:deep(.markdown-body table td) {
  border-color: #d0d7de !important;
}

:deep(.markdown-body table tr) {
  background-color: transparent !important;
}

:deep(.markdown-body table tr:nth-child(2n)) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
